import PropTypes from "prop-types";
import styled, { keyframes, css } from "styled-components";
import tokens from "../../theme/tokens";

const { brand, border, surface, radius, spacing, typography } = tokens;

// -- shimmer primitives ------------------------------------------------------

// Scoped keyframes — matches the pattern used in MailIntakeStyled and
// MatterDetailsSkeleton so we don't leak a global animation name.
const shimmerKeyframes = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const shimmer = css`
  background: linear-gradient(
    90deg,
    ${surface.hover} 0%,
    ${border.light} 50%,
    ${surface.hover} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmerKeyframes} 1.2s ease-in-out infinite;
`;

// $h: number → "<n>px"
// $w: number → "<n>px"; string (e.g. "60%") → pass through.
const toCssLen = (v, fallback) => {
  if (v == null) return fallback;
  return typeof v === "number" ? `${v}px` : v;
};

const Bar = styled.div`
  height: ${({ $h }) => toCssLen($h, "12px")};
  width: ${({ $w }) => toCssLen($w, "100%")};
  border-radius: ${radius.sm};
  ${shimmer};
`;

const Pill = styled(Bar)`
  border-radius: 9999px;
  height: ${({ $h }) => toCssLen($h, "20px")};
`;

// -- layout shell ------------------------------------------------------------

// Must match MatterDetailsWrapper's outer layout so the shell → real-content
// swap doesn't shift the sidebar width, tab-strip vertical position, or the
// main content boundary. Reads identical spacing / surface tokens as the
// real chrome.
const ShellWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 10px 0 0 0;
  box-sizing: border-box;
  overflow: hidden;
`;

// Mirrors SideBarWrapper in MatterDetailsStyled.js: same width (220px
// compact default), radius, padding, height, and flex-shrink: 0. Any drift
// here re-introduces a layout jump when the real sidebar mounts.
const SidebarCol = styled.div`
  display: flex;
  flex-direction: column;
  background: ${surface.surface};
  border-top-left-radius: ${radius.lg};
  border-top-right-radius: ${radius.lg};
  padding: 12px 14px;
  height: calc(100vh - 20px);
  width: 220px;
  min-width: 200px;
  flex-shrink: 0;
  margin-right: 8px;
  gap: 16px;
  box-sizing: border-box;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const TreeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const TreeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: ${({ $depth }) => ($depth || 0) * 14}px;
`;

const Glyph = styled.div`
  width: 14px;
  height: 14px;
  border-radius: ${radius.sm};
  ${shimmer};
  flex-shrink: 0;
`;

// Mirrors MainContentWrapper: flex:1, min-width:0 so it shrinks correctly
// next to the fixed-width sidebar shell.
const MainCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Mirrors the real tab strip height + bottom border treatment so the
// 2px active-tab indicator doesn't ever appear to "drop in" when the real
// TabView mounts. Height matches TabSectionContainer in TabViewStyled.js.
const TabStrip = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  height: 40px;
  padding: 0 12px;
  background: ${surface.page};
  border-bottom: 1px solid ${border.default};
  flex-shrink: 0;
`;

// The first (collapse) tab is always present and inactive-ish; give it
// a neutral placeholder that doesn't carry the 2px brand indicator.
const CollapseTabPlaceholder = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${radius.sm};
  ${shimmer};
  flex-shrink: 0;
`;

// Active tab placeholder — matches the real TabItem active-state:
// brand top-bar + white surface + text shimmer inside. Using a solid
// 2px top border (vs transparent) so the visual "you are here" rail
// appears immediately, exactly where the real TabView will render it.
const ActiveTabPlaceholder = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  min-width: 180px;
  max-width: 220px;
  background: ${surface.surface};
  border-top: 2px solid ${brand.primary};
  border-left: 1px solid ${border.default};
  border-right: 1px solid ${border.default};
  border-top-left-radius: ${radius.md};
  border-top-right-radius: ${radius.md};
  flex-shrink: 0;
`;

const CanvasArea = styled.div`
  flex: 1;
  min-height: 0;
  background: ${surface.surface};
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow: hidden;
`;

// Content-row placeholder — intentionally generic so it reads as "a document
// is loading" without pretending to be a specific layout (table vs PDF vs
// markdown). The table-shaped MatterDetailsSkeleton at
// src/components/Table/MatterDetailsSkeleton.js takes over once the real
// TabView mounts and lands on the matter-list tab.
const ContentLine = styled(Bar)`
  height: ${({ $h }) => toCssLen($h, "14px")};
`;

const Caption = styled.div`
  font-family: ${typography.fontFamily};
  font-size: 11px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: 0.04em;
  color: ${tokens.text.tertiary};
  text-transform: uppercase;
`;

/**
 * Full-page loading chrome for `MatterDetails`.
 *
 * Replaces the old single-spinner affordance (`<InlineLoader />` next to the
 * collapse button) with a layout-stable three-region shimmer:
 *
 *   [ sidebar tree ] [ tab strip ]
 *                    [ canvas    ]
 *
 * Widths/padding/heights mirror the real chrome (`SideBarWrapper`,
 * `MainContentWrapper`, `TabSectionContainer`) so swapping to the real
 * components produces no visible layout jump. This component is intentionally
 * scoped to MatterDetails — other pages should define their own shell
 * skeletons keyed to their own chrome.
 *
 * `showSidebar` mirrors the real-chrome `showSideBar` state (which reads
 * `localStorage.sidebar`) so a user who previously collapsed the sidebar
 * doesn't see a 220px sidebar region flash in during loading and vanish
 * when the real chrome mounts. Defaults to `true` for Storybook / standalone
 * use; the MatterDetails call site always passes the current value.
 */
const MatterDetailsShellSkeleton = ({ rows = 7, showSidebar = true }) => {
  // Deterministic width variance so each tree row looks like a real
  // folder/file name without re-shuffling on every render.
  const treeWidths = [72, 58, 64, 50, 68, 42, 60, 66, 54, 62];
  // Mix of roots and nested children — suggests a folder tree without
  // committing to any specific shape.
  const depths = [0, 1, 1, 0, 1, 2, 0, 1, 1, 0];

  return (
    <ShellWrapper aria-busy="true" aria-live="polite" role="status">
      <span
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
        }}
      >
        Loading matter…
      </span>

      {showSidebar && (
        <SidebarCol>
          <SidebarHeader>
            <Bar $w="60%" $h={12} />
            <Glyph />
          </SidebarHeader>
          <TreeList>
            {Array.from({ length: rows }).map((_, idx) => (
              <TreeItem
                key={idx}
                $depth={depths[idx % depths.length]}
                data-testid="shell-skeleton-tree-row"
              >
                <Glyph />
                <Bar $w={`${treeWidths[idx % treeWidths.length]}%`} $h={10} />
              </TreeItem>
            ))}
          </TreeList>
        </SidebarCol>
      )}

      <MainCol>
        <TabStrip>
          <CollapseTabPlaceholder />
          <ActiveTabPlaceholder>
            <Glyph />
            <Bar $w="70%" $h={10} />
          </ActiveTabPlaceholder>
        </TabStrip>
        <CanvasArea>
          <Caption as="div">
            <Bar $w="140px" $h={10} />
          </Caption>
          <ContentLine $w="88%" />
          <ContentLine $w="72%" />
          <ContentLine $w="80%" />
          <ContentLine $w="66%" />
          <Pill $w="120px" />
          <ContentLine $w="90%" />
          <ContentLine $w="55%" />
          <ContentLine $w="82%" />
        </CanvasArea>
      </MainCol>
    </ShellWrapper>
  );
};

MatterDetailsShellSkeleton.propTypes = {
  rows: PropTypes.number,
  showSidebar: PropTypes.bool,
};

export default MatterDetailsShellSkeleton;
